<ph-dialog [open]="open" size="xl" heading="Create payable" (ph-close)="onDialogClose()">
    <div class="steps" [class.processing]="isProcessing">
        <ph-stepper [items]="stepLabels" [current]="step" (ph-step)="onStepPicked($event)"></ph-stepper>

        <!-- First Step -->
        <div class="step" [hidden]="step !== 0">
            <ph-columns min="360px">
                <div [class.ocr-locked]="isExtractingInvoice">
                    <ph-card heading="Upload payable invoice" fill flat>
                        <ph-button *ngIf="invoiceAttachment"
                                   slot="actions"
                                   variant="error"
                                   mode="stroke"
                                   size="xsmall"
                                   icon="trash"
                                   (click)="discardInvoice()">Remove invoice</ph-button>

                        <ng-container *ngIf="invoiceAttachment; else uploadNew">
                            <!--
                                SD-7656: once the file is uploaded, the invoice is still being read
                                server-side (OCR, ~20-30s) and the detail form loads vendors + expense
                                categories. Keep the SAME existing loader visible over the invoice area
                                until EVERYTHING settles — the OCR read (isProcessing) AND the form's
                                vendor/category loads (payableDetailForm.isLoading) — then reveal the
                                preview. No new/second loader; the preview is only shown once it's all loaded.
                            -->
                            <ph-spinner *ngIf="isProcessing || payableDetailForm?.isLoading; else invoicePreview"
                                        size="44"
                                        center
                                        label="Reading your invoice…"
                                        data-test="invoice-reading-overlay"></ph-spinner>
                            <ng-template #invoicePreview>
                                <app-payable-attachment-carousel
                                        [attachments]="[invoiceAttachment]">
                                </app-payable-attachment-carousel>
                            </ng-template>
                        </ng-container>
                        <ng-template #uploadNew>
                            <app-upload-invoice-form
                                    [payable]="localPayable"
                                    (invoiceUploadingEvent)="handleInvoiceUploadingEvent()"
                                    (invoiceUploadErrorEvent)="handleInvoiceUploadErrorEvent()"
                                    (invoiceUploadedEvent)="handleInvoiceUploadedEvent($event)">
                            </app-upload-invoice-form>
                        </ng-template>
                    </ph-card>
                </div>

                <app-payable-detail-form
                    #payableDetailForm
                    [localPayable]="localPayable"
                    [organizationId]="organizationId"
                    [preselectedVendorId]="data?.vendorId"
                    [isProcessing]="isProcessing"
                >
                </app-payable-detail-form>
            </ph-columns>
        </div>

        <!-- Second Step -->
        <div class="step" [hidden]="step !== 1">
            <ph-card heading="Additional details" narrow flat [formGroup]="payableActionFormGroup">
                <ph-alert *ngIf="hasApprovalRule" status="information" variant="lighter">
                    {{ invoiceActionInfoBannerText }}
                </ph-alert>
                <ph-alert *ngIf="isZeroAmount" status="information" variant="lighter">
                    This payable has a $0 amount. Only "Mark as Paid" and "Save as Draft" are available.
                </ph-alert>

                <!-- Invoice Action Section -->
                <ph-field>
                    <ph-label required>What would you like to do with this invoice?</ph-label>
                    <ph-radio-group formControlName="invoiceAction"
                                    ngDefaultControl
                                    [items]="invoiceActionItems"></ph-radio-group>
                    <lf-control-messages hint [control]="payableActionFormGroup.get('invoiceAction')"></lf-control-messages>
                </ph-field>

                <!-- Payment Method Section -->
                <ng-container *ngIf="payableActionFormGroup.get('invoiceAction')?.value === PayableActionType.SCHEDULE">
                    <ph-field>
                        <ph-label required>Payment Method</ph-label>
                        <ph-hint *ngIf="verifiedBillPayAccounts.length === 0">
                            <a routerLink="/app/payables/settings/bill-pay-bank-accounts">
                                {{ bankAccounts.length > 0 ? 'Verify' : 'Create' }} a bill pay account to enable mail/print check options
                            </a>
                        </ph-hint>
                        <ph-radio-group formControlName="paymentMethod"
                                        ngDefaultControl
                                        [items]="paymentMethodItems"></ph-radio-group>
                        <lf-control-messages hint [control]="payableActionFormGroup.get('paymentMethod')"></lf-control-messages>
                    </ph-field>

                    <ph-hint *ngIf="!isVendorAchEnabled && hasTreasuryAccount">
                        This vendor isn't set up for ACH yet. Enable ACH in
                        <ph-link-button inline variant="primary" (click)="navigateToVendorSettings($event)">vendor settings</ph-link-button>.
                    </ph-hint>

                    <!-- SD-10162: the ACH option's own reasons. A kit radio option is a label only, so they sit
                         under the group. A review still under way is not one of them: ACH stays selectable
                         and Continue answers with Approval Required. -->
                    <ph-hint *ngIf="isVendorAchEnabled && hasTreasuryAccount && !isZeroAmount && achExceedsBalance" has-error>
                        {{ achExceedsBalanceMessage }}
                    </ph-hint>
                    <ph-hint *ngIf="isVendorAchEnabled && hasTreasuryAccount && achSwitchedOff">
                        Please contact PayHOA to enable ACH transfers for this organization.
                    </ph-hint>

                    <!-- While the org profile is still loading we cannot yet know whether the org has a
                         complete address, so show a neutral "checking…" state rather than the misleading
                         "address required" warning (which flashed for a valid org until the GET resolved) (SD-7438). -->
                    <ph-alert *ngIf="isOrganizationProfileLoading && verifiedBillPayAccounts.length > 0"
                              status="information"
                              variant="lighter">
                        Checking organization mailing address…
                    </ph-alert>

                    <!-- Load failed (or timed out): we couldn't read the address, so show a distinct
                         "couldn't verify — retry" state instead of the "address required" warning, which
                         would mislead when the real cause is a failed GET, not a missing address (SD-7438, AC #3). -->
                    <ph-alert *ngIf="!isOrganizationProfileLoading && organizationProfileLoadFailed && verifiedBillPayAccounts.length > 0"
                              status="warning"
                              variant="lighter">
                        Couldn't verify your organization's mailing address.
                        <ph-link-button variant="primary" size="small" (click)="getOrganizationProfile()">Retry</ph-link-button>
                        to enable the mailed check option.
                    </ph-alert>

                    <ph-alert *ngIf="!isOrganizationProfileLoading && !organizationProfileLoadFailed && !hasCompleteOrgAddress && verifiedBillPayAccounts.length > 0"
                              status="warning"
                              variant="lighter">
                        Organization address required to mail a check.
                        <ph-link-button variant="primary" size="small" (click)="openOrgAddressModal()">Set address here</ph-link-button>
                        to enable the mailed check option.
                    </ph-alert>

                    <ph-alert *ngIf="vendorNameTooLongForCheck" status="warning" variant="lighter" data-test="vendor-name-too-long-alert">
                        <span>A mailed check can print at most 40 characters of the vendor name, and
                        "{{ payableDetailForm?.selectedVendor?.name }}" is longer. Shorten it or move part (such as a DBA) to Nickname in
                        <ph-link-button inline variant="primary" (click)="navigateToVendorSettings($event)">vendor settings</ph-link-button>
                        to enable the mailed check option.</span>
                    </ph-alert>

                    <ph-alert *ngIf="payableActionFormGroup.get('paymentMethod')?.value === PaymentMethod.PRINT_CHECK"
                              status="information"
                              variant="lighter">
                        <strong>QuickBooks check formats supported.</strong>
                        <span>We support Voucher and Standard (3-per-page) formats. Order check stock from QuickBooks:
                        <a href="https://intuitmarket.intuit.com/checks/voucher-checks" target="_blank" rel="noopener noreferrer">Voucher Checks</a>
                        or <a href="https://intuitmarket.intuit.com/checks/standard-checks" target="_blank" rel="noopener noreferrer">Standard Checks (3-per-page)</a>.</span>
                    </ph-alert>

                    <ph-alert *ngIf="hasApprovalRule && payableActionFormGroup.get('paymentMethod')?.value === PaymentMethod.PRINT_CHECK"
                              status="information"
                              variant="lighter">
                        The check will be available for printing if approved.
                        You'll receive an email notification when it's ready to print.
                    </ph-alert>

                    <!-- ACH Transfer Info -->
                    <ng-container *ngIf="payableActionFormGroup.get('paymentMethod')?.value === PaymentMethod.ACH_TRANSFER">
                        <ph-field *ngIf="selectedVendorBankAccounts.length > 1">
                            <ph-label required>Vendor bank account</ph-label>
                            <ph-select formControlName="vendorBankAccountId"
                                       ngDefaultControl
                                       [items]="vendorBankAccountItems"></ph-select>
                            <lf-control-messages hint [control]="payableActionFormGroup.get('vendorBankAccountId')"></lf-control-messages>
                        </ph-field>
                        <ph-alert *ngIf="selectedAchBankAccount" status="information" variant="lighter">
                            Payment will be sent via ACH from your Treasury Balance account to {{ payableDetailForm?.selectedVendor?.name }}'s bank account ending in ****{{ selectedAchBankAccount.last4 }}.
                        </ph-alert>
                    </ng-container>

                    <ng-container *ngIf="payableActionFormGroup.get('paymentMethod').dirty">
                        <!-- Bank Account Section (for check methods) -->
                        <ph-field *ngIf="payableActionFormGroup.get('paymentMethod').value !== PaymentMethod.VENDOR_DECIDE && payableActionFormGroup.get('paymentMethod').value !== PaymentMethod.ACH_TRANSFER">
                            <ph-label required>Payment bank account</ph-label>
                            <ph-toolbar>
                                <app-lob-bank-account-selector
                                        [formGroup]="payableActionFormGroup"
                                        [lobBankAccounts]="verifiedBillPayAccounts"
                                        controlName="bankAccountId"
                                >
                                </app-lob-bank-account-selector>
                                <ph-button variant="secondary"
                                           mode="filled"
                                           icon="plus"
                                           [disabled]="isProcessing"
                                           (click)="addBankAccount()">Add bank account</ph-button>
                            </ph-toolbar>
                            <lf-control-messages hint [control]="payableActionFormGroup.get('bankAccountId')"></lf-control-messages>
                        </ph-field>

                        <ng-container *ngIf="payableActionFormGroup.get('paymentMethod').value === PaymentMethod.MAIL_CHECK">
                            <!-- Check Number for Mail Check -->
                            <app-check-number-input
                                [formGroup]="payableActionFormGroup"
                                controlName="checkNumber"
                                bankAccountControlName="bankAccountId"
                                [bankAccounts]="verifiedBillPayAccounts"
                            ></app-check-number-input>

                            <!-- Check Stub Message for Mail Check -->
                            <ph-field>
                                <ph-label>Message (will appear on check stub)</ph-label>
                                <ph-textarea formControlName="checkStubMessage"
                                             ngDefaultControl
                                             rows="3"
                                             maxlength="400"
                                             placeholder="Payment check stub message…"></ph-textarea>
                                <ph-hint>Limit 400 characters</ph-hint>
                                <lf-control-messages hint [control]="payableActionFormGroup.get('checkStubMessage')"></lf-control-messages>
                            </ph-field>
                        </ng-container>

                        <ng-container *ngIf="payableActionFormGroup.get('paymentMethod').value === PaymentMethod.PRINT_CHECK; else paymentDateSelection">
                            <!-- Printable Check Section -->
                            <ph-columns min="220px">
                                <ph-field>
                                    <ph-label required>Check Date</ph-label>
                                    <ph-date-input formControlName="checkDate"
                                                   ngDefaultControl
                                                   [hasError]="payableActionFormGroup.get('checkDate').touched && payableActionFormGroup.get('checkDate').invalid"></ph-date-input>
                                    <lf-control-messages hint [control]="payableActionFormGroup.get('checkDate')"></lf-control-messages>
                                </ph-field>
                                <app-check-number-input
                                    [formGroup]="payableActionFormGroup"
                                    controlName="checkNumber"
                                    bankAccountControlName="bankAccountId"
                                    [bankAccounts]="bankAccounts"
                                ></app-check-number-input>
                            </ph-columns>
                            <ph-field>
                                <ph-label>Memo</ph-label>
                                <ph-textarea rows="2"
                                             ngDefaultControl
                                             [(ngModel)]="localPayable.memo"
                                             [ngModelOptions]="{standalone: true}"
                                             placeholder="Enter memo (optional)"></ph-textarea>
                            </ph-field>
                            <app-check-format-options #checkFormatOptions></app-check-format-options>
                        </ng-container>

                        <!-- Payment Date Section (ACH and mail check both support immediate or scheduled) -->
                        <ng-template #paymentDateSelection>
                            <ph-field>
                                <ph-label required>Payment Date</ph-label>
                                <ph-radio-group formControlName="whenToPay"
                                                ngDefaultControl
                                                [items]="whenToPayItems"></ph-radio-group>
                                <lf-control-messages hint [control]="payableActionFormGroup.get('whenToPay')"></lf-control-messages>
                            </ph-field>

                            <ph-field *ngIf="payableActionFormGroup.get('whenToPay')?.value === PaymentSchedule.NO_EARLIER_THAN">
                                <ph-label required>Earliest Payment Date</ph-label>
                                <ph-date-input formControlName="earliestPaymentDate"
                                               ngDefaultControl
                                               [min]="minDay"
                                               [hasError]="payableActionFormGroup.get('earliestPaymentDate').touched && payableActionFormGroup.get('earliestPaymentDate').invalid"></ph-date-input>
                                <ph-hint>Payment processed at 11:55AM EST on the scheduled date.</ph-hint>
                                <lf-control-messages hint [control]="payableActionFormGroup.get('earliestPaymentDate')"></lf-control-messages>
                            </ph-field>
                        </ng-template>
                    </ng-container>
                </ng-container>
            </ph-card>
        </div>

        <!-- Final Step -->
        <div class="step" [hidden]="step !== 2">
            <ph-columns min="360px">
                <app-mark-as-paid-form
                    *ngIf="payableActionFormGroup.get('invoiceAction')?.value === PayableActionType.MARK_PAID"
                    #markAsPaidFormComponent
                    [localPayable]="localPayable"
                    [potentialTransactionMatches]="potentialTransactionMatches"
                    (journalEntrySummaryChange)="markAsPaidJournalEntrySummary = $event">
                </app-mark-as-paid-form>
                <app-payable-review
                    *ngIf="localPayable"
                    [narrow]="payableActionFormGroup.get('invoiceAction')?.value !== PayableActionType.MARK_PAID"
                    [payable]="localPayable"
                    [invoiceAction]="payableActionFormGroup.get('invoiceAction')?.value"
                    [paymentMethod]="payableActionFormGroup.get('paymentMethod')?.value"
                    [whenToPay]="payableActionFormGroup.get('whenToPay')?.value"
                    [paymentDate]="formattedPaymentDateForReview"
                    [journalEntrySummary]="payableActionFormGroup.get('invoiceAction')?.value === PayableActionType.MARK_PAID ? markAsPaidJournalEntrySummary : null">
                </app-payable-review>
            </ph-columns>
        </div>
    </div>

    <ph-actions slot="footer" align="between">
        <ph-button variant="secondary" mode="filled" (click)="step === 0 ? closeDialog() : goToStep(step - 1)">
            {{ step === 0 ? 'Cancel' : 'Back' }}
        </ph-button>
        <ph-button *ngIf="step === 0" [disabled]="isProcessing || payableDetailForm?.isLoading" (click)="handleFirstStep()">
            Continue
        </ph-button>
        <ph-button *ngIf="step === 1" [disabled]="isProcessing" (click)="handleSecondStep()">
            Continue
        </ph-button>
        <ph-button *ngIf="step === 2" [disabled]="isProcessing" (click)="handleFinalStep()">
            {{ confirmButtonLabel }}
        </ph-button>
    </ph-actions>
</ph-dialog>

<app-add-bill-pay-account-dialog *ngIf="addBankAccountOpen"
                                 [open]="addBankAccountOpen"
                                 size="lg"
                                 (closed)="onAddBankAccountClosed($event)"></app-add-bill-pay-account-dialog>

<app-confirm-dialog [open]="!!confirmRequest"
                    [heading]="confirmRequest?.heading"
                    [message]="confirmRequest?.message"
                    [confirmLabel]="confirmRequest?.confirmLabel"
                    [cancelLabel]="confirmRequest?.cancelLabel || 'Cancel'"
                    [destructive]="!!confirmRequest?.destructive"
                    (closed)="onConfirmClosed($event)"></app-confirm-dialog>

<app-approval-required-dialog [open]="approvalRequiredOpen" (closed)="approvalRequiredOpen = false"></app-approval-required-dialog>
